import { useDispatch, useSelector } from 'react-redux';
import { CONTENT_SAVE_REQUEST_FAILED } from '../../../redux/actions/actionTypes';
import { contentPublishFailed, updateOptimisticLockingForRequest, contentPublishSucceeded, requestedContentPublish
// @ts-expect-error Untyped module
} from '../../../redux/actions/appActions';
// @ts-expect-error Untyped module
import { logSentryWithRecentReduxHistory } from '../../../redux/actions/errorActions';
import { EditorMetricsTracker } from '../../../utils/metricsTracker';
import { getIsFormValid } from '../../../redux/selectors/formStatusSelectors';
import { PublishActionTypes } from '../types/publishButtonTypes';
import { useCallback, useMemo } from 'react';
import http from 'hub-http/clients/apiClient';
import { convertCMPObjectToInstanceObject } from '../utils/objectConversion';
import { PublishTypes } from '../../../constants/PublishTypes';
import useContentCrmObject from './useContentCrmObject';
const IGNORE_ERROR_CODE = [401, 403];
const cmpSchedulePublishUnpublishAPICallback = ({
  contentObjectType,
  objectId,
  schedulePublishDate,
  scheduleUnpublishDate,
  unpublishExpiryUrl
}) => {
  return http.postWithResponse(`content-management/publication/v1/${contentObjectType}/${objectId}/schedule/publish-and-unpublish/async`, {
    data: {
      schedulePublishDate,
      scheduleUnpublishDate,
      unpublishExpiryUrl
    }
  });
};
const cmpPublishUnpublishAPICallback = ({
  isUnpublish,
  contentObjectType,
  objectId,
  overridePublishDate
}) => {
  return http.postWithResponse(`content-management/publication/v1/${contentObjectType}/${objectId}/${isUnpublish ? 'unpublish' : 'publish'}`, {
    data: {
      overridePublishDate
    }
  });
};
export const useCMPPublishActionHandlers = ({
  endTourIfAny,
  saveAction,
  onPublishSuccess,
  onPublishError,
  trackPublishEvent,
  onUnpublishSuccess,
  onUnpublishError,
  contentObjectType,
  objectId,
  shouldConvertCMPObjectToInstanceObject
}) => {
  const {
    contentCrmObject,
    contentObjectType: contentCrmObjectType,
    reconcileContentCrmObjectForPublishActions,
    contentCrmObjectId,
    canScheduleWithContentCrmObject
  } = useContentCrmObject();
  const dispatch = useDispatch();
  const isFormValid = useSelector(getIsFormValid);
  const dispatchUpdateOptimisticLocking = useCallback((xhr, hasSaved) => {
    dispatch(updateOptimisticLockingForRequest({
      hasSaved,
      xLastModifiedTimestampHeader: xhr.getResponseHeader('x-last-modified-timestamp')
    }));
  }, [dispatch]);
  const actionHandler = useCallback(async ({
    isUnpublish = false,
    isScheduling = false,
    schedulePublishDate,
    scheduleUnpublishDate,
    publishType,
    unpublishExpiryUrl,
    overridePublishDate
  } = {}) => {
    if (endTourIfAny) {
      endTourIfAny();
    }
    if (!isFormValid) {
      return;
    }
    dispatch(requestedContentPublish());
    try {
      const {
        type
      } = await dispatch(saveAction({
        isSaveBeforePublish: true
      }));
      if (type === CONTENT_SAVE_REQUEST_FAILED) {
        dispatch(contentPublishFailed());
        // return early if save failed to prevent publishing with unsaved changes
        return;
      }
      let updatedCrmObject = Object.assign({}, contentCrmObject);
      if (isScheduling) {
        const {
          data,
          xhr
        } = await cmpSchedulePublishUnpublishAPICallback({
          contentObjectType: contentCrmObjectType || contentObjectType,
          objectId: contentCrmObjectId || objectId,
          schedulePublishDate,
          scheduleUnpublishDate,
          unpublishExpiryUrl
        });
        dispatchUpdateOptimisticLocking(xhr, true);
        if (data.schedulePublishLocator) {
          reconcileContentCrmObjectForPublishActions(PublishActionTypes.SCHEDULE_PUBLISH, {
            schedulePublishDate
          });
        }
        if (data.scheduleUnpublishLocator) {
          reconcileContentCrmObjectForPublishActions(PublishActionTypes.SCHEDULE_UNPUBLISH, {
            scheduleUnpublishDate,
            unpublishExpiryUrl
          });
        }
      } else {
        const {
          data,
          xhr
        } = await cmpPublishUnpublishAPICallback({
          isUnpublish,
          contentObjectType: contentCrmObjectType || contentObjectType,
          objectId: contentCrmObjectId || objectId,
          overridePublishDate
        });
        dispatchUpdateOptimisticLocking(xhr, true);
        updatedCrmObject = data;
        reconcileContentCrmObjectForPublishActions(isUnpublish ? PublishActionTypes.UNPUBLISH : PublishActionTypes.PUBLISH);
      }
      const successData = !canScheduleWithContentCrmObject && shouldConvertCMPObjectToInstanceObject ? convertCMPObjectToInstanceObject(updatedCrmObject, contentCrmObjectType || contentObjectType) : updatedCrmObject;
      if (isUnpublish) {
        onUnpublishSuccess(successData);
      } else {
        onPublishSuccess(successData, publishType);
      }
      trackPublishEvent(true, publishType);
      dispatch(contentPublishSucceeded());
    } catch (errorResponse) {
      if (!IGNORE_ERROR_CODE.includes(errorResponse.status)) {
        EditorMetricsTracker.counter('publish-errors').increment();
        dispatch(logSentryWithRecentReduxHistory('Publish errors', {
          errorResponse: errorResponse.responseJSON,
          errorStatus: errorResponse.status
        }, 'publishError'));
      }
      if (isUnpublish) {
        onUnpublishError(errorResponse);
      } else {
        onPublishError(errorResponse);
      }
      trackPublishEvent(false, publishType);
      if (errorResponse && errorResponse.xhr) {
        dispatchUpdateOptimisticLocking(errorResponse.xhr, false);
      }
      dispatch(contentPublishFailed());
    }
  }, [endTourIfAny, isFormValid, dispatch, saveAction, contentCrmObject, canScheduleWithContentCrmObject, shouldConvertCMPObjectToInstanceObject, contentCrmObjectType, contentObjectType, trackPublishEvent, contentCrmObjectId, objectId, dispatchUpdateOptimisticLocking, reconcileContentCrmObjectForPublishActions, onUnpublishSuccess, onPublishSuccess, onUnpublishError, onPublishError]);
  return useMemo(() => ({
    publish: (publishType, __scheduleUpdateDate, overridePublishDate) => {
      actionHandler({
        publishType,
        overridePublishDate
      }).catch(errorResponse => onPublishError(errorResponse));
    },
    unpublish: () => {
      actionHandler({
        isUnpublish: true
      }).catch(errorResponse => onUnpublishError(errorResponse));
    },
    schedulePublishUnpublish: ({
      schedulePublishDate,
      scheduleUnpublishDate,
      unpublishExpiryUrl
    }) => {
      actionHandler({
        schedulePublishDate,
        scheduleUnpublishDate,
        unpublishExpiryUrl,
        isScheduling: true,
        publishType: PublishTypes.schedule
      }).catch(errorResponse => {
        if (schedulePublishDate) {
          onPublishError(errorResponse);
        }
        if (scheduleUnpublishDate) {
          onUnpublishError(errorResponse);
        }
      });
    }
  }), [actionHandler, onPublishError, onUnpublishError]);
};
const usePublishActionHandlers = ({
  endTourIfAny,
  publishUnpublishAPICallback,
  saveAction,
  onPublishSuccess,
  onPublishError,
  trackPublishEvent,
  onUnpublishSuccess,
  onUnpublishError
}) => {
  const dispatch = useDispatch();
  const isFormValid = useSelector(getIsFormValid);
  const actionHandler = useCallback(({
    publishType,
    isUnpublish = false
  } = {}) => {
    if (endTourIfAny) {
      endTourIfAny();
    }
    if (!isUnpublish && !isFormValid) {
      return;
    }
    dispatch(requestedContentPublish());
    dispatch(saveAction({
      isSaveBeforePublish: true
    })).then(({
      type
    }) => {
      if (type === CONTENT_SAVE_REQUEST_FAILED) {
        dispatch(contentPublishFailed());
        return;
      }
      publishUnpublishAPICallback(isUnpublish).then(({
        data,
        xhr
      }) => {
        dispatch(updateOptimisticLockingForRequest({
          hasSaved: true,
          xLastModifiedTimestampHeader: xhr.getResponseHeader('x-last-modified-timestamp')
        }));
        if (isUnpublish) {
          onUnpublishSuccess(data);
        } else {
          onPublishSuccess(data, publishType);
        }
        trackPublishEvent(true, publishType);
        dispatch(contentPublishSucceeded());
      }).catch((errorResponse = {}) => {
        if (!IGNORE_ERROR_CODE.includes(errorResponse.status)) {
          EditorMetricsTracker.counter('publish-errors').increment();
          dispatch(logSentryWithRecentReduxHistory('Publish errors', {
            errorResponse: errorResponse.responseJSON,
            errorStatus: errorResponse.status
          }, 'publishError'));
        }
        if (isUnpublish) {
          onUnpublishError(errorResponse);
        } else {
          onPublishError(errorResponse);
        }
        trackPublishEvent(false, publishType);
        dispatch(updateOptimisticLockingForRequest({
          hasSaved: false,
          xLastModifiedTimestampHeader: errorResponse.xhr.getResponseHeader('x-last-modified-timestamp')
        }));
        dispatch(contentPublishFailed());
      });
    }).catch(() => {
      dispatch(contentPublishFailed());
    });
  }, [endTourIfAny, isFormValid, dispatch, saveAction, publishUnpublishAPICallback, onUnpublishSuccess, onPublishSuccess, trackPublishEvent, onUnpublishError, onPublishError]);
  return useMemo(() => ({
    publish: publishType => actionHandler({
      publishType
    }),
    unpublish: () => actionHandler({
      isUnpublish: true
    })
  }), [actionHandler]);
};
export default usePublishActionHandlers;